import { ReactElement, useCallback } from 'react'
import { useWatch } from 'react-hook-form'
import { Matrix4 } from 'three'
import { Alert, Stack, Typography } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import {
  CoordinatePointInput,
  createUnlimitedPrecisionPointManipulationConfig,
  TextField,
} from '@ui/forms'

interface Props {
  applyIfcAlignmentConfig: boolean
}

const FormFields = ({ applyIfcAlignmentConfig }: Props): ReactElement => {
  const referencePointIFCCS = useWatch({ name: 'referencePointIFCCS' })
  const referencePointModelCS = useWatch({ name: 'referencePointModelCS' })
  const zRotationDegrees = useWatch({ name: 'zRotationDegrees' })

  const transformPIFCSceneMarkerPoint = useCallback(
    (markerPoint: ImmutableVector3) => {
      if (!applyIfcAlignmentConfig) return markerPoint

      const PIFCRef = new ImmutableVector3(
        referencePointIFCCS?.x ?? 0,
        referencePointIFCCS?.y ?? 0,
        referencePointIFCCS?.z ?? 0,
      )
      const R = new Matrix4().makeRotationZ(((Number(zRotationDegrees) || 0) * Math.PI) / 180)
      const PModelRef = new ImmutableVector3(
        referencePointModelCS?.x ?? 0,
        referencePointModelCS?.y ?? 0,
        referencePointModelCS?.z ?? 0,
      )

      return markerPoint.sub(PIFCRef).applyMatrix4(R).add(PModelRef)
    },
    [applyIfcAlignmentConfig, referencePointIFCCS, referencePointModelCS, zRotationDegrees],
  )

  return (
    <Stack direction="column" spacing={1}>
      {applyIfcAlignmentConfig && (
        <Alert severity="info" sx={{ flexShrink: 0 }}>
          „Ausrichtung anwenden“ deaktiveren, um einen Punkt für P<sub>IFC,ref</sub> aus der Szene
          abzugreifen.
        </Alert>
      )}
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography>
          P<sub>IFC,ref</sub>
        </Typography>
        <CoordinatePointInput
          label="IFC Referenzpunkt"
          name="referencePointIFCCS"
          transformSceneMarkerPoint={transformPIFCSceneMarkerPoint}
          disableReplace={applyIfcAlignmentConfig}
          pointManipulation={createUnlimitedPrecisionPointManipulationConfig()}
        />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography>Rotation</Typography>
        <TextField
          name="zRotationDegrees"
          type="number"
          label="Rotation um Z (°)"
          inputLabel="Rotation um Z (Grad)"
          size="small"
          sx={{ width: 180 }}
        />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography>
          P<sub>Modell,ref</sub>
        </Typography>
        <CoordinatePointInput
          label="Modell Referenzpunkt"
          name="referencePointModelCS"
          pointManipulation={createUnlimitedPrecisionPointManipulationConfig()}
        />
      </Stack>
    </Stack>
  )
}

export default FormFields
